import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import AuctionCalendarIcon from '@/shared-components/icons/components/forms/AuctionCalendarIcon';
import CalendarIcon from '@/shared-components/icons/components/forms/CalendarIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import ArtworkAuctionEndDatePopup from './components/ArtworkAuctionEndDatePopup';
import ArtworkConfirmationDialog from './components/ArtworkConfirmationDialog';
import ArtworkPublicationDatePopup from './components/ArtworkPublicationDatePopup';
import ChangesRequiredDisplayer from './components/ChangesRequiredDisplayer';
import SaveDraft from './components/SaveDraft';
import StepAccordeon from './components/StepAccordeon';
import ArtworkAgreementCheckboxes from './submit-button/components/ArtworkAgreementCheckboxes';
import About from './about';
import Media from './media';
import Shipping from './shipping';
import SubmitButton from './submit-button';

import './index.css';

export default function AddArtworkForm({ artworkData = null, previewMode = false, editMode = false }) {
  const t = useTranslations('common.forms.add_artwork');
  const location = useLocation();
  const permissions = location.state?.permissions || {};
  const { state, stateHandler, cacheHandler } = useStateHandler();
  const navigate = useNavigate();
  
  // State for confirmation dialog when leaving
  const [goBackConfirmation, setGoBackConfirmation] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  
  // State for artwork agreement acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [auctionAgreementAccepted, setAuctionAgreementAccepted] = useState(false);
  
  // State for form validation errors
  const [error, setError] = useState(null);
  const [validationFocus, setValidationFocus] = useState(null);
  
  // Stores the initial form state for comparison
  const [formState, setFormState] = useState(
    Utils.initArtworkFormState(artworkData, editMode, state)
  );
  const initialState = useRef(structuredClone(formState));
  const [openPublicationDate, setOpenPublicationDate] = useState(false);
  const [openAuctionEndDate, setOpenAuctionEndDate] = useState(false);
  
  // Hook for navigating back
  const goBackHandler = useGoBack("/profile");
  const username = state?.user_session?.user_username?.toLowerCase?.();
  const isAuctionAccount = username === 'tgallery';
  const session = state?.user_session;
  const userType = session?.user_type;
  const isArtistAccount = userType === 'artist' || userType === 'gallery_artist';
  const isGalleryMember = Boolean(session?.gallery_member);
  const galleryId = session?.gallery_id || artworkData?.artwork_about?.artwork_gallery?._id;
  // Dynamically swap the price field so the curated auction account captures
  // the starting bid instead of the standard list price.
  const priceFieldKey = useMemo(
    () => (isAuctionAccount ? 'price_starting_at' : 'artwork_price'),
    [isAuctionAccount],
  );
  const galleryPermissionCheck = useCallback(
    (permissionKey) => canAccessGalleryPermission(session, permissionKey, galleryId),
    [session, galleryId],
  );
  const hasSchedulePermission = !editMode || !isGalleryMember
    ? true
    : galleryPermissionCheck('artworks.edit_schedule');
  // New artwork forms historically do not pass the member permission callback to
  // their child sections. Keep that legacy behavior isolated, but pass the
  // specific server-authorized capability required by AI autocomplete.
  const canUseAiAutocomplete = !isGalleryMember || galleryPermissionCheck('artworks.add');

  const hasScheduledPublication = Boolean(formState.publicationDate && formState.publicationTime);
  const isGalleryOnboarding =
    state?.user_session?.user_type === 'gallery' &&
    state?.user_session?.user_flags?.onboarding_completed === false;
  const canUseSchedule =
    !previewMode &&
    (!editMode || formState.initialDraftStatus === 'queued') &&
    !isGalleryOnboarding;
  const isCalendarActive = canUseSchedule && hasScheduledPublication;
  const hasAuctionEndDate = Boolean(formState.auctionEndsDate && formState.auctionEndsTime);
  const isAuctionCalendarActive = canUseSchedule && isAuctionAccount && hasAuctionEndDate;

  useEffect(() => {
    if (!isAuctionAccount) {
      setAuctionAgreementAccepted(false);
    }
  }, [isAuctionAccount]);

  // ✅ Prevent page refresh or closing tab
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = ''; // Standard browser warning
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const focusInvalidStep = useCallback((step) => {
    setValidationFocus(current => ({
      step,
      requestId: (current?.requestId ?? 0) + 1,
    }));
  }, []);

  return (
    <>
      <div className="add-artwork-form__container">
        <div className="add-artwork-form__wrap">
          <div className="add-artwork-form__header">
            {canUseSchedule && !editMode && (
              <div className="add-artwork-form__header-left-actions">
                <SaveDraft
                  formState={formState}
                  setFormState={setFormState}
                  initialState={initialState}
                  buttonFormat={false}
                />
              </div>
            )}

            <Text className="add-artwork-form__header-text">
              {previewMode
                ? t('title_request')
                : editMode
                  ? t('title_edit')
                  : isAuctionAccount
                    ? t('title_auction')
                    : t('title_add')}
            </Text>

            <div className="add-artwork-form__header-controls">
              {canUseSchedule && (
                <div className="add-artwork-form__header-actions">
                  <button
                    type="button"
                    className={`add-artwork-form__calendar-button${isCalendarActive ? ' add-artwork-form__calendar-button--active' : ''}`}
                    onClick={() => {
                      if (!hasSchedulePermission) {
                        setShowActionRejectedPopup(true);
                        return;
                      }
                      setOpenPublicationDate(true);
                    }}
                    aria-label="Schedule artwork publication date"
                    aria-pressed={isCalendarActive}
                    aria-disabled={!hasSchedulePermission}
                  >
                    <CalendarIcon className="add-artwork-form__calendar-icon" />
                  </button>

                  {isAuctionAccount && (
                    <button
                      type="button"
                      className={`add-artwork-form__calendar-button add-artwork-form__calendar-button--auction${isAuctionCalendarActive ? ' add-artwork-form__calendar-button--active' : ''}`}
                      onClick={() => {
                        if (!hasSchedulePermission) {
                          setShowActionRejectedPopup(true);
                          return;
                        }
                        setOpenAuctionEndDate(true);
                      }}
                      aria-label="Set auction end date"
                      aria-pressed={isAuctionCalendarActive}
                      aria-disabled={!hasSchedulePermission}
                    >
                      <AuctionCalendarIcon className="add-artwork-form__calendar-icon add-artwork-form__calendar-icon--auction" />
                    </button>
                  )}
                </div>
              )}

              {/* Exit Button with Change Detection */}
              <XIcon 
                onClick={() => {
                  const hasChanges = JSON.stringify(initialState.current) !== JSON.stringify(formState);
                  
                  if (hasChanges) {
                    setGoBackConfirmation(true);
                  } else {
                    goBackHandler();
                  }
                }}
              />
            </div>
          </div>

          {/* Show required changes if artworkData contains them */}
          {Boolean(artworkData?.draft_changes) && <ChangesRequiredDisplayer changesRequired={artworkData.draft_changes}/>}

          <Text className="add-artwork-form__text" paragraph>
            {t('introduction')}
          </Text>

          <form className="add-artwork-form__form" id="add-artwork-form">
            
            {/* Step 1: About */}
            <StepAccordeon
              lastCompletedStep={formState.lastCompletedStep}
              previewMode={previewMode}
              editMode={editMode}
              step={1}
              header={t('sections.about')}
              activeClassName="add-artwork-about-accordeon__active"
              validationFocus={validationFocus}
            >
              <About
                formState={formState}
                setFormState={setFormState}
                editMode={editMode}
                previewMode={previewMode}
                formError={error}
                setFormError={setError}
                permissions={permissions}
                galleryPermissionCheck={isGalleryMember && editMode ? galleryPermissionCheck : null}
                canUseAiAutocomplete={canUseAiAutocomplete}
                priceFieldKey={priceFieldKey}
                isAuctionAccount={isAuctionAccount}
              />
            </StepAccordeon>

            {/* Step 2: Media */}
            <StepAccordeon 
              lastCompletedStep={formState.lastCompletedStep} 
              previewMode={previewMode} 
              editMode={editMode} 
              step={2} 
              header={t('sections.media')}
              activeClassName="add-artwork-media-accordeon__active" 
              validationFocus={validationFocus}
            >
              <Media 
                formState={formState} 
                setFormState={setFormState} 
                editMode={editMode} 
                previewMode={previewMode} 
                formError={error} 
                setFormError={setError}
                permissions={permissions}
                galleryPermissionCheck={isGalleryMember && editMode ? galleryPermissionCheck : null}
              />
            </StepAccordeon>

            {/* Step 3: Shipping */}
            <StepAccordeon 
              lastCompletedStep={formState.lastCompletedStep} 
              previewMode={previewMode} 
              editMode={editMode} 
              step={3} 
              header={t('sections.shipping')}
              activeClassName="add-artwork-shipping-accordeon__active" 
              validationFocus={validationFocus}
              permissions={permissions}
              locked={
                isArtistAccount
                && !(editMode && Object.keys(permissions).length > 0 && permissions.shippingTariffs)
              }
            >
              <Shipping 
                formState={formState} 
                setFormState={setFormState} 
                editMode={editMode} 
                previewMode={previewMode} 
                formError={error} 
                setFormError={setError}
                permissions={permissions} 
                galleryPermissionCheck={isGalleryMember && editMode ? galleryPermissionCheck : null}
              />
              {!previewMode && !editMode && (
                <ArtworkAgreementCheckboxes
                  formError={error}
                  setFormError={setError}
                  termsAccepted={termsAccepted}
                  setTermsAccepted={setTermsAccepted}
                  isAuctionAccount={isAuctionAccount}
                  auctionAgreementAccepted={auctionAgreementAccepted}
                  setAuctionAgreementAccepted={setAuctionAgreementAccepted}
                />
              )}
            </StepAccordeon>

            {/* Submit Button */}
            {!previewMode && (
              <SubmitButton
                formState={formState} 
                formError={error} 
                setFormError={setError} 
                editMode={editMode} 
                termsAccepted={termsAccepted} 
                auctionAgreementAccepted={auctionAgreementAccepted}
                onInvalidStep={focusInvalidStep}
                setGoBackConfirmation={setGoBackConfirmation}
                initialState={initialState}
              />
            )}
          </form>
        </div>
      </div>
      {/* Artwork Confirmation Dialog */}
      <ArtworkConfirmationDialog 
        open={goBackConfirmation} 
        onClose={() => {
          setGoBackConfirmation(false); 
        }}
        onConfirm={goBackHandler} 
        confirmText={t('exit_confirm')}
        formState={formState} 
        setFormState={setFormState} 
        initialState={initialState}
        previewMode={previewMode}
        editMode={editMode}
        formError={error}
        setFormError={setError} 
        navigate={navigate}
        cacheHandler={cacheHandler}
        stateHandler={stateHandler}
      >
        <Text className="add-artwork-form__confirmation-text-header" paragraph>
          {t('exit_title')}
        </Text>

        <Text className="add-artwork-form__confirmation-text" paragraph>
          {editMode ? t('exit_body_edit') : t('exit_body')}
        </Text>
      </ArtworkConfirmationDialog>
      <ArtworkPublicationDatePopup
        open={openPublicationDate}
        onClose={() => setOpenPublicationDate(false)}
        formState={formState}
        setFormState={setFormState}
        formError={error}
        setFormError={setError}
      />
      <ActionRejectedPopup
        open={showActionRejectedPopup}
        close={() => setShowActionRejectedPopup(false)}
        description="You do not have permission to update the artwork schedule. Please contact your gallery admin."
      />
      {isAuctionAccount && (
        <ArtworkAuctionEndDatePopup
          open={openAuctionEndDate}
          onClose={() => setOpenAuctionEndDate(false)}
          formState={formState}
          setFormState={setFormState}
          formError={error}
          setFormError={setError}
        />
      )}
    </>
  );
}
